/**
 * Runtime v3 — definitions and their lifecycle vocabulary.
 *
 * A definition is CONTENT, held by a registry that never reads it. The
 * registry enforces identity (digest), order (status transitions), pinning
 * (exact versions only) and authorship (an actor on every transition). What a
 * definition MEANS is the compiler's and the engine's business.
 *
 * This is the structural answer to "definitions are code"
 * (`metric-registry.ts:2003`, synced at boot by
 * `governed-metric-registry-lifecycle.service.ts:126-192`): nothing here seeds
 * a definition, and nothing enumerates the set from a compiled constant.
 */

import type {
  ContentDigest,
  IsoInstant,
  RuntimeV3JsonObject,
  RuntimeV3PeriodGrain,
  RuntimeV3SchemaVersion,
} from './common';
/**
 * The period-basis vocabulary is I14-00's (`operation-registry.ts`), which
 * already needs it for `runtimeV3PeriodBasisCorresponds`. I14-01 consumes it
 * here rather than defining a second copy: two arrays with the same values
 * can drift with nothing catching it (Opus HOLD @ 9a98ea2d15, finding 4).
 */
import type { RuntimeV3PeriodBasis } from './operation-registry';

export const RUNTIME_V3_DEFINITION_KINDS = [
  /** A measurable quantity and how it is computed from a revision's measures. */
  'metric',
  /** A named decision procedure expressed as a graph of operators. */
  'capability',
  /** A mapping from a definition's inputs onto a specific revision's refs. */
  'binding',
  /** A parameterized use of a capability, pinned to bindings and revisions. */
  'instance',
  /** Declarative source decoding and validation conventions (RA-14). */
  'adapter',
  /** Revision-pinned, bounded source text using the same governed lifecycle. */
  'document_source',
] as const;
export type RuntimeV3DefinitionKind = (typeof RUNTIME_V3_DEFINITION_KINDS)[number];

export const RUNTIME_V3_DEFINITION_STATUSES = [
  'draft',
  'validated',
  'approved',
  'active',
  'retired',
  /**
   * A draft abandoned before review. Terminal, audited, and never selectable
   * — the honest resting place for a row a failed or superseded authoring
   * left behind, as against deleting it and losing the fact that it existed.
   */
  'discarded',
] as const;
export type RuntimeV3DefinitionStatus = (typeof RUNTIME_V3_DEFINITION_STATUSES)[number];

export const RUNTIME_V3_DEFINITION_TRANSITIONS = [
  'author',
  /**
   * A draft edited in place. It stays a draft — the status does not move —
   * but it IS a change of content by someone, so it gets its own audit row
   * rather than silently replacing the digest of a reviewed draft.
   */
  'amend',
  'validate',
  'approve',
  'activate',
  'retire',
  /** A draft abandoned before review. Reachable only from `draft`. */
  'discard_draft',
] as const;
export type RuntimeV3DefinitionTransition = (typeof RUNTIME_V3_DEFINITION_TRANSITIONS)[number];

export const RUNTIME_V3_DEFINITION_SCOPE_KINDS = ['shared', 'tenant'] as const;
export type RuntimeV3DefinitionScopeKind = (typeof RUNTIME_V3_DEFINITION_SCOPE_KINDS)[number];

/**
 * A SUBSET PIN — depends on a NAMED SLICE of a keyed collection inside the
 * depended-on definition's payload, never the whole artifact (#8752, ending
 * the semantic_families re-pin cascade).
 *
 * `collection` names the array field in `payload` (e.g. `"families"`).
 * `keys` are the identifying values of the entries actually used, matched
 * against each entry's IDENTITY FIELD — the sole field on the entry whose
 * name ends `"Ref"`, the convention every keyed collection in this registry
 * already follows (`familyRef`, `relationshipRef`, `definitionRef`, …); an
 * entry with zero or several such fields cannot be addressed this way and
 * is refused rather than guessed at. `digest` is the canonical digest of
 * exactly those entries, sorted by their identity field — stable under
 * reordering of `keys` or of the source collection, and unaffected by any
 * entry NOT named in `keys`.
 *
 * The whole point: a dependent that pins one family's worth of
 * `semantic_families` does not care that a sibling family was added, edited
 * or removed, so activating a new `semantic_families` version does not
 * strand it and it never needs re-pinning. `collection` and `keys` are
 * generic — read from the pin itself, never a fixed artifact, family or
 * kind name in control flow — so this mechanism serves any keyed collection,
 * not only families.
 */
export type RuntimeV3DefinitionVersionRefSubset = Readonly<{
  collection: string;
  keys: readonly string[];
  digest: ContentDigest;
}>;

/**
 * ONE EXACT CONTENT, IN ONE EXACT SCOPE — OR ONE EXACT SLICE OF ONE.
 *
 * A dependency pins by DIGEST, never by version number. A version is a fact
 * about one registry's authoring history — `metric@3` here is `metric@1` in a
 * fresh database — so a dependency pinned by version resolves to the wrong
 * definition somewhere else, or to nothing at all. The digest is the same
 * wherever the content is the same, which is exactly what lets a binding and
 * the metrics it depends on publish into an empty registry and still find each
 * other.
 *
 * `scopeKind` and `tenantId` are part of the reference, not decoration. A ref
 * carrying only `definitionRef + digest` would force resolution to search
 * every tenant and take whichever row matched — which lets a tenant definition
 * depend on a FOREIGN tenant's definition by quoting its digest. With scope on
 * the reference, resolution addresses exactly one row and reachability becomes
 * an invariant the registry can enforce.
 *
 * EXACTLY ONE of `digest` (a WHOLE pin) or `subset` (a SUBSET pin, above) is
 * present. A whole pin resolves to whichever stored row carries that exact
 * content, of any status; a subset pin resolves only against the ACTIVE row
 * for `definitionRef` in this scope, by its subset digest — see
 * `computeCollectionSubsetDigest` (`canonical-json.ts`), the one function
 * that computes it, shared by the loader tooling, the registry guards and
 * their tests. Every artifact written before this pin form existed carries a
 * whole pin, so nothing already stored changes meaning.
 */
export type RuntimeV3DefinitionVersionRef = Readonly<{
  definitionRef: string;
  scopeKind: RuntimeV3DefinitionScopeKind;
  /** `null` if and only if `scopeKind` is `shared`. */
  tenantId: string | null;
  /** Present on a WHOLE pin; absent on a SUBSET pin (see `subset` below). */
  digest?: ContentDigest;
  /** Present on a SUBSET pin; absent on a WHOLE pin. */
  subset?: RuntimeV3DefinitionVersionRefSubset;
}>;

/**
 * The one short fingerprint for a reference — a WHOLE pin's own digest, or a
 * SUBSET pin's collection digest. THE ONE SOURCE for this question: lineage
 * projection (`result-envelope.ts`) and every dataset CLI's `load` review
 * output call this rather than repeating `ref.digest ?? ref.subset?.digest ?? ''`,
 * which read "neither present" the same as an empty-but-valid digest and
 * silently printed a truncated fingerprint instead of refusing (review round
 * 2, 2026-09-24). Exactly one of the two is present for any reference that
 * passed authoring (`pinShapeFailure`); one with neither is malformed, not
 * merely blank, and this throws rather than papering over it.
 */
export function definitionVersionRefFingerprint(ref: RuntimeV3DefinitionVersionRef): string {
  if (typeof ref.digest === 'string') return ref.digest;
  if (typeof ref.subset?.digest === 'string') return ref.subset.digest;
  throw new Error(
    `definition-version reference for "${ref.definitionRef}" carries neither a whole nor a subset digest`,
  );
}

/**
 * Definition content, held OPAQUELY.
 *
 * The registry validates the envelope — kind, scope, dependencies, digest —
 * and never the payload's business meaning. That is what keeps "no definition
 * seeded from code" true: there is no shape here for code to fill in.
 */
export type RuntimeV3DefinitionPayload = RuntimeV3JsonObject;

/**
 * The content a digest is taken over. Everything that changes the MEANING of
 * the definition is inside; nothing that changes only its lifecycle position
 * is. That is why approving, activating and retiring never move the digest.
 */
export type RuntimeV3DefinitionContent = Readonly<{
  definitionRef: string;
  kind: RuntimeV3DefinitionKind;
  scopeKind: RuntimeV3DefinitionScopeKind;
  tenantId: string | null;
  payload: RuntimeV3DefinitionPayload;
  dependencies: readonly RuntimeV3DefinitionVersionRef[];
}>;

export type RuntimeV3DefinitionRecord = Readonly<{
  schemaVersion: RuntimeV3SchemaVersion;
  id: string;
  definitionRef: string;
  kind: RuntimeV3DefinitionKind;
  scopeKind: RuntimeV3DefinitionScopeKind;
  tenantId: string | null;
  version: number;
  status: RuntimeV3DefinitionStatus;
  digest: ContentDigest;
  payload: RuntimeV3DefinitionPayload;
  dependencies: readonly RuntimeV3DefinitionVersionRef[];
  /**
   * `dependencies` EXACTLY AS STORED, before `parseDefinitionDependencies`
   * mapped it — optional because only a row read from the registry's own
   * table carries it. A pre-fold-12 row's stored entries may still carry a
   * per-dependency `version` field the mapped `RuntimeV3DefinitionVersionRef`
   * shape does not, and the legacy digest algorithm hashed that field: a
   * verifier that reconstructs "as stored" from the mapped array would
   * silently drop it and never reproduce the row's real digest.
   */
  dependenciesJson?: readonly RuntimeV3JsonObject[] | null;
  createdAt: IsoInstant;
  createdBy: string;
  validatedAt: IsoInstant | null;
  validatedBy: string | null;
  approvedAt: IsoInstant | null;
  approvedBy: string | null;
  activatedAt: IsoInstant | null;
  activatedBy: string | null;
  retiredAt: IsoInstant | null;
  retiredBy: string | null;
}>;

/**
 * The audit row. One per transition, actor recorded, never updated and never
 * deleted: the history of a definition is the evidence that its approved
 * content was approved by someone.
 */
export type RuntimeV3DefinitionEvent = Readonly<{
  id: string;
  definitionId: string;
  definitionRef: string;
  version: number;
  transition: RuntimeV3DefinitionTransition;
  fromStatus: RuntimeV3DefinitionStatus | null;
  toStatus: RuntimeV3DefinitionStatus;
  digest: ContentDigest;
  actorId: string;
  occurredAt: IsoInstant;
  detail?: RuntimeV3JsonObject;
}>;

/** The author input. `version` is assigned by the registry, never by the caller. */
export type RuntimeV3AuthorDefinitionInput = Readonly<{
  definitionRef: string;
  kind: RuntimeV3DefinitionKind;
  scopeKind: RuntimeV3DefinitionScopeKind;
  tenantId: string | null;
  payload: RuntimeV3DefinitionPayload;
  dependencies: readonly RuntimeV3DefinitionVersionRef[];
}>;

/** The selector a reader uses to pick an active definition. */
export type RuntimeV3DefinitionSelector = Readonly<{
  definitionRef: string;
  kind: RuntimeV3DefinitionKind;
  /** `null` selects the shared-scope definition. */
  tenantId: string | null;
}>;

export const RUNTIME_V3_CLOSED_PERIOD_AUTHORITIES =
  ['revision_coverage_end', 'declared_close_date'] as const;
export const RUNTIME_V3_DEFAULT_PERIOD_RULES =
  ['latest_closed_period', 'latest_as_of', 'none'] as const;

/** Authored policy only. Coverage and gaps come from the pinned revision. */
export type RuntimeV3MeasurePeriodDeclaration = Readonly<{
  measureRef: string;
  periodBasis: RuntimeV3PeriodBasis;
  nativeGrain: RuntimeV3PeriodGrain;
  closedPeriodAuthority: (typeof RUNTIME_V3_CLOSED_PERIOD_AUTHORITIES)[number];
  defaultPeriodRule: (typeof RUNTIME_V3_DEFAULT_PERIOD_RULES)[number];
}>;

/** Population-binding payload.periodDeclarations: exactly one entry per measure ref. */
export type RuntimeV3DefinitionPeriodDeclaration =
  Readonly<Record<string, RuntimeV3MeasurePeriodDeclaration>>;
